import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { FileText, Plus, Upload, Wallet, Clock, FolderPlus, Folder as FolderIcon } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { Button } from '@/shared/components/ui/Button';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { useUser } from '@/shared/hooks/useAuth';
import { useSalesDocs, useSalesDocMutations, useSalesDocFolders, useSalesDocFolderMutations } from '../hooks/useSalesDocs';
import { SalesDocFormModal } from '../components/SalesDocFormModal';
import { SalesDocDrawer } from '../components/SalesDocDrawer';
import { RecordPaymentModal } from '../components/RecordPaymentModal';
import { detectFileType, deriveFileName } from '../constants';
import { getUploaderId } from '../types';
import type { CreateSalesDocPayload, SalesDoc, UpdateSalesDocPayload } from '../types';
import { formatINR } from '@/shared/utils/currency';

/** Generated docs are titled "<number> — <client>" (see logGeneratedSalesDoc /
 *  logGeneratedInvoiceSalesDoc on the backend) — split that back apart so the
 *  table can give each its own column. A manually-uploaded doc with no
 *  reference_number just shows its whole title as the client/label. */
function clientNameFor(d: SalesDoc): string {
  const prefix = d.reference_number ? `${d.reference_number} — ` : '';
  return prefix && d.title.startsWith(prefix) ? d.title.slice(prefix.length) : d.title;
}

function fmtDate(iso?: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Gradient icon-chip treatment shared with the dashboard StatCard widget
// (frontend/src/features/dashboard/infrastructure/widgets/StatCard.tsx) —
// same tokens (success/warning/danger/info/primary), no new colors.
export function SalesDocsPage() {
  const user = useUser();
  const role = user?.role ?? '';
  const isPrivileged = role === 'superadmin' || (role === 'admin' && user?.department === 'accounts');

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  // null = "All" (no folder filter), 'unfiled' = docs outside any folder,
  // otherwise a real folder id.
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const { data: docs = [], isLoading, isError } = useSalesDocs({ folderId: selectedFolderId });
  const { createDoc, updateDoc, deleteDoc } = useSalesDocMutations();
  const { data: folders = [] } = useSalesDocFolders();
  const { createFolder, deleteFolder } = useSalesDocFolderMutations();

  const [formOpen, setFormOpen] = useState(false);
  const [editDoc, setEditDoc] = useState<SalesDoc | null>(null);
  const [drawerDoc, setDrawerDoc] = useState<SalesDoc | null>(null);
  const [confirmDoc, setConfirmDoc] = useState<SalesDoc | null>(null);
  const [recordPaymentDoc, setRecordPaymentDoc] = useState<SalesDoc | null>(null);

  const canManage = (doc: SalesDoc) => isPrivileged || (!!user?.id && getUploaderId(doc.uploaded_by) === user.id);

  const folderNameById = useMemo(() => new Map(folders.map((f) => [f.id, f.name])), [folders]);

  // Filter options derived from the loaded list (real/derived-real values only).
  const typeOptions = useMemo(() => {
    const set = new Set(docs.map((d) => d.type).filter(Boolean) as string[]);
    return [{ value: '', label: 'All types' }, ...Array.from(set).sort().map((t) => ({ value: t, label: t }))];
  }, [docs]);

  const ownerOptions = useMemo(() => {
    const set = new Set(docs.map((d) => d.creator_name).filter(Boolean) as string[]);
    return [{ value: '', label: 'All owners' }, ...Array.from(set).sort().map((o) => ({ value: o, label: o }))];
  }, [docs]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (typeFilter && d.type !== typeFilter) return false;
      if (ownerFilter && d.creator_name !== ownerFilter) return false;
      if (!q) return true;
      return [d.title, d.description, d.creator_name, fmtDate(d.created_at), deriveFileName(d.url), detectFileType(d.url).label].some(
        (f) => (f ?? '').toLowerCase().includes(q),
      );
    });
  }, [docs, search, typeFilter, ownerFilter]);

  // Summary stats from the full docs list already fetched by useSalesDocs
  // above — no new API calls.
  const totalValue = useMemo(() => docs.reduce((s, d) => s + (d.amount || 0), 0), [docs]);
  const awaitingPaymentCount = useMemo(
    () => docs.filter((d) => d.type === 'invoice' && d.status !== 'paid').length,
    [docs],
  );

  const handleCreate = () => {
    setEditDoc(null);
    setFormOpen(true);
  };
  const handleEdit = (doc: SalesDoc) => {
    setDrawerDoc(null);
    setEditDoc(doc);
    setFormOpen(true);
  };

  const onCreate = (payload: CreateSalesDocPayload) =>
    createDoc.mutate(payload, {
      onSuccess: () => {
        toast.success('Document added.');
        setFormOpen(false);
      },
      onError: () => toast.error('Failed to add document.'),
    });

  const onUpdate = (id: string, payload: UpdateSalesDocPayload) =>
    updateDoc.mutate(
      { id, payload },
      {
        onSuccess: () => {
          toast.success('Document updated.');
          setFormOpen(false);
          setEditDoc(null);
        },
        onError: () => toast.error('Failed to update document.'),
      },
    );

  const handleDelete = () => {
    if (!confirmDoc) return;
    deleteDoc.mutate(confirmDoc.id, {
      onSuccess: () => {
        toast.success('Document deleted.');
        setConfirmDoc(null);
        setDrawerDoc(null);
      },
      onError: () => toast.error('Failed to delete document.'),
    });
  };

  const columns: Column<SalesDoc>[] = [
    {
      key: 'reference_number',
      header: 'Document Number',
      render: (d) => {
        const ft = detectFileType(d.url);
        const Icon = ft.icon;
        const number = d.reference_number || '—';
        return (
          <span className="flex items-center gap-2">
            <Icon className="h-4 w-4 shrink-0 text-on-surface-variant" />
            {d.url ? (
              <a
                href={d.url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="font-medium text-primary hover:underline"
              >
                {number}
              </a>
            ) : (
              <span className="font-medium text-on-surface">{number}</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'title',
      header: 'Client Name',
      render: (d) => <span className="font-medium text-on-surface">{clientNameFor(d)}</span>,
    },
    { 
      key: 'type', 
      header: 'Type', 
      render: (d) => {
        const t = d.type || 'other';
        const label = t.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        return <Badge tone={t === 'proposal' ? 'primary' : t === 'quotation' ? 'success' : t === 'invoice' ? 'warning' : 'neutral'}>{label}</Badge>;
      } 
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (d) => {
        if (!d.amount) return '—';
        return formatINR(d.amount);
      }
    },
    {
      key: 'status',
      header: 'Status',
      render: (d) => {
        const s = d.status || 'draft';
        // split('_') handles the new 'partially_paid' value the same way the
        // Accounts module's own status labels are formatted, without
        // affecting any single-word existing status.
        const label = s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        const isExpired = d.valid_until && new Date(d.valid_until) < new Date() && !['finalized', 'paid', 'accepted', 'rejected'].includes(s);
        const toneMap: Record<string, any> = {
          draft: 'neutral',
          sent: 'primary',
          accepted: 'success',
          finalized: 'success',
          paid: 'success',
          // Additive — client-approved audit fix (SalesDoc payments now route
          // through the real Accounts allocation/posting engine).
          partially_paid: 'warning',
          rejected: 'critical',
          expired: 'critical',
          overdue: 'critical'
        };
        return (
          <div className="flex flex-col items-start gap-1">
            <Badge tone={isExpired ? 'critical' : toneMap[s] || 'neutral'}>{isExpired ? 'Expired' : label}</Badge>
            {d.valid_until && (
              <span className="text-data-xs text-on-surface-variant">Until: {fmtDate(d.valid_until)}</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'folder',
      header: 'Folder',
      render: (d) => {
        const name = d.folderId ? folderNameById.get(d.folderId) : null;
        return name ? (
          <span className="flex items-center gap-1 text-body-sm text-on-surface-variant">
            <FolderIcon className="h-3.5 w-3.5" /> {name}
          </span>
        ) : (
          <span className="text-body-sm text-on-surface-variant/60">Unfiled</span>
        );
      },
    },
    { key: 'creator_name', header: 'Uploaded By', render: (d) => d.creator_name || '—' },
    { key: 'created_at', header: 'Uploaded', render: (d) => fmtDate(d.created_at) },
  ];

  return (
    <div className="mx-auto flex h-full w-full max-w-[1580px] flex-col gap-5 pb-16">
      <PageHeader
        icon={FileText}
        eyebrow="Accounts"
        title="Sales Documents"
        description={`Quotations, proformas and tax invoices with their files — ${visible.length} document${visible.length !== 1 ? 's' : ''} in view.`}
        actions={<>
          <Button variant="outline" onClick={() => (window.location.href = '/documents/create?type=quotation')}>
            <Plus className="h-4 w-4" /> Quotation
          </Button>
          <Button variant="outline" onClick={() => (window.location.href = '/documents/create?type=proforma')}>
            <Plus className="h-4 w-4" /> Proforma
          </Button>
          <Button onClick={handleCreate}><Upload className="h-4 w-4" /> Upload File</Button>
        </>}
      />

      {/* Folders — flat, shared library (client-approved audit follow-up).
          Documents are filed by choosing a folder from each row's "Move"
          action; nothing here nests folders inside folders. */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setSelectedFolderId(null)}
          className={cn(
            'rounded-full border px-3 py-1.5 text-body-sm font-medium transition-colors',
            selectedFolderId === null
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-outline text-on-surface-variant hover:bg-surface-container-low',
          )}
        >
          All
        </button>
        <button
          type="button"
          onClick={() => setSelectedFolderId('unfiled')}
          className={cn(
            'rounded-full border px-3 py-1.5 text-body-sm font-medium transition-colors',
            selectedFolderId === 'unfiled'
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-outline text-on-surface-variant hover:bg-surface-container-low',
          )}
        >
          Unfiled
        </button>
        {folders.map((f) => (
          <div key={f.id} className="group relative">
            <button
              type="button"
              onClick={() => setSelectedFolderId(f.id)}
              className={cn(
                'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-body-sm font-medium transition-colors',
                selectedFolderId === f.id
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-outline text-on-surface-variant hover:bg-surface-container-low',
              )}
            >
              <FolderIcon className="h-3.5 w-3.5" />
              {f.name}
              <span className="text-on-surface-variant/70">({f.docCount})</span>
            </button>
            {isPrivileged && (
              <button
                type="button"
                title="Delete folder (documents inside become Unfiled)"
                onClick={() => {
                  if (window.confirm(`Delete folder "${f.name}"? Its documents will become Unfiled, not deleted.`)) {
                    deleteFolder.mutate(f.id, {
                      onSuccess: () => {
                        toast.success('Folder deleted.');
                        if (selectedFolderId === f.id) setSelectedFolderId(null);
                      },
                      onError: () => toast.error('Failed to delete folder.'),
                    });
                  }
                }}
                className="absolute -right-1.5 -top-1.5 hidden h-4 w-4 items-center justify-center rounded-full bg-danger text-data-xs leading-none text-white group-hover:flex"
              >
                ×
              </button>
            )}
          </div>
        ))}
        {isPrivileged && (
          <button
            type="button"
            onClick={() => {
              const name = window.prompt('New folder name:');
              if (name && name.trim()) {
                createFolder.mutate(name.trim(), {
                  onSuccess: () => toast.success('Folder created.'),
                  onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to create folder.'),
                });
              }
            }}
            className="flex items-center gap-1.5 rounded-full border border-dashed border-outline px-3 py-1.5 text-body-sm font-medium text-on-surface-variant transition-colors hover:bg-surface-container-low"
          >
            <FolderPlus className="h-3.5 w-3.5" /> New Folder
          </button>
        )}
      </div>

      <StatGrid cols={3}>
        <StatCard label="Total Documents" value={docs.length} hint="All sales documents" icon={FileText} tone="primary" />
        <StatCard label="Total Value" value={formatINR(totalValue)} hint="Across all documents" icon={Wallet} tone="info" />
        <StatCard label="Awaiting Payment" value={awaitingPaymentCount} hint="Unpaid invoices" icon={Clock} tone="warning" />
      </StatGrid>

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-72">
          <Input name="docs-search" placeholder="Search title, type, owner…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="w-44">
          <Select name="docs-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} options={typeOptions} aria-label="Filter by type" />
        </div>
        <div className="w-48">
          <Select name="docs-owner" value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} options={ownerOptions} aria-label="Filter by owner" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center"><Spinner /></div>
      ) : isError ? (
        <EmptyState title="Failed to load documents" description="Check your connection or try refreshing." />
      ) : visible.length === 0 ? (
        <EmptyState
          title={search || typeFilter || ownerFilter ? 'No matching documents' : 'No documents yet'}
          description={search || typeFilter || ownerFilter ? 'Try different filters or search term.' : 'Add a document link or upload a file.'}
          action={!search && !typeFilter && !ownerFilter ? <Button onClick={handleCreate}>New Document</Button> : undefined}
        />
      ) : (
        <div className="flex-1 overflow-y-auto">
          <Table columns={columns} data={visible} rowKey={(d) => d.id} onRowClick={setDrawerDoc} empty="No documents found." />
        </div>
      )}

      <SalesDocFormModal
        open={formOpen}
        doc={editDoc}
        loading={createDoc.isPending || updateDoc.isPending}
        onClose={() => {
          setFormOpen(false);
          setEditDoc(null);
        }}
        onCreate={onCreate}
        onUpdate={onUpdate}
      />

      <SalesDocDrawer
        doc={drawerDoc}
        canManage={!!drawerDoc && canManage(drawerDoc)}
        canRecordPayment={
          !!drawerDoc &&
          (user?.role === 'superadmin' ||
            user?.department === 'accounts' ||
            user?.department === 'sales' ||
            user?.department === 'general_manager' ||
            Boolean(user?.username && String(user.username).toLowerCase().startsWith('bdm'))) &&
          (drawerDoc.type === 'invoice' || drawerDoc.type === 'proforma')
        }
        deleting={deleteDoc.isPending}
        folders={folders}
        onMoveToFolder={(d, folderId) =>
          updateDoc.mutate(
            { id: d.id, payload: { folderId } },
            {
              onSuccess: () => toast.success(folderId ? 'Moved to folder.' : 'Moved to Unfiled.'),
              onError: () => toast.error('Failed to move document.'),
            },
          )
        }
        onClose={() => setDrawerDoc(null)}
        onEdit={handleEdit}
        onDelete={setConfirmDoc}
        onRecordPayment={(d) => {
          setRecordPaymentDoc(d);
          setDrawerDoc(null);
        }}
      />

      <RecordPaymentModal
        open={!!recordPaymentDoc}
        invoice={recordPaymentDoc}
        onClose={() => setRecordPaymentDoc(null)}
      />

      <ConfirmDialog
        open={!!confirmDoc}
        title="Delete document"
        message={`Delete "${confirmDoc?.title ?? ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={deleteDoc.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDoc(null)}
      />

    </div>
  );
}
